import { Component, Input, inject, SecurityContext, OnInit } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { makeStateKey, TransferState } from '@angular/core';
import { CommonModule } from '@angular/common';
import { isPlatformServer } from '@angular/common';
import { PLATFORM_ID } from '@angular/core';
import { KlaDataService } from '../tools/services/kla.data.service';

const getDynamicContentKey = (id: string) => makeStateKey<string>(`dynamic-content-${id}`);

@Component({
  selector: 'app-dynamic-content',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div [innerHTML]="sanitizedContent"></div>
  `
})
export class DynamicContentComponent implements OnInit {
  @Input() contentId!: string;
  @Input() set htmlContent(value: string) {
    this.processContent(value);
  }

  private uniqueId = Math.random().toString(36).substring(2, 10);
  private sanitizer = inject(DomSanitizer);
  private transferState = inject(TransferState);
  private platformId = inject(PLATFORM_ID);
  
  sanitizedContent: SafeHtml | null = null;

  constructor(private dataService: KlaDataService) {}

  ngOnInit() {
  //   // Check if content exists in TransferState on client-side
    if (!isPlatformServer(this.platformId)) {
      const stateKey = getDynamicContentKey(`${this.contentId}-${this.uniqueId}`);
      if (this.transferState && this.transferState.hasKey(stateKey)) {
        const savedContent = this.transferState.get(stateKey, '');
        this.sanitizedContent = this.sanitizer.bypassSecurityTrustHtml(savedContent);
        this.transferState.remove(stateKey);
      }
    }
  }

  private processContent(content: string) {
    if (!content || content === "") return;
    let processedContent = content;

    const stateKey = getDynamicContentKey(`${this.contentId}-${this.uniqueId}`);
    if (!content.trim().startsWith('<')) {
      processedContent = `<p>${content}</p>`;
    }

    processedContent = this.replaceApiImageUrls(processedContent);
    processedContent = processedContent.replace(/\s*title="⚠[^"]*"/gi, '');
    processedContent = processedContent.replace(/<span[^>]*class="kla-alt-warning"[^>]*>[\s\S]*?<\/span>/gi, '');
    
    // Store in TransferState only during SSR
    if (isPlatformServer(this.platformId)) {
      this.transferState.set(stateKey, processedContent);
    }

    this.sanitizedContent = this.sanitizer.bypassSecurityTrustHtml(processedContent);
  }

  private replaceApiImageUrls(html: string): string {
    const apiDomain = this.dataService.getApiConfig().server.replace(/\/+$/, '');
    const cdnDomain = this.dataService.getImageConfig().server.replace(/\/+$/, '');
    
    const escapedApiDomain = this.escapeRegExp(apiDomain);
  
    const regex = new RegExp(
      `<(img|video)([^>]*?)\\s+src=(["'])${escapedApiDomain}`,
      'g'
    );
  
    return html.replace(regex, `<$1$2 src=$3${cdnDomain}`);
  }
  
  private escapeRegExp(str: string): string {
    return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }
}